import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { FolderPlus, FileText } from 'lucide-react';

export interface FolderDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (folderName: string, description: string) => Promise<void>;
    initialFolderName?: string;
    title?: string;
}

const FolderDialog: React.FC<FolderDialogProps> = ({
                                                       isOpen,
                                                       onClose,
                                                       onSubmit,
                                                       initialFolderName = '',
                                                       title = 'Create New Folder'
                                                   }) => {
    const [folderName, setFolderName] = useState(initialFolderName);
    const [description, setDescription] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (isOpen) {
            setFolderName(initialFolderName);
            setDescription('');
            setError(null);
        }
    }, [isOpen, initialFolderName]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const trimmedName = folderName.trim();

        if (!trimmedName) {
            setError('Folder name is required');
            return;
        }

        setIsSubmitting(true);
        setError(null);

        try {
            await onSubmit(trimmedName, description.trim());
            setIsSubmitting(false);  // Add this line
            handleClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to create folder');
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setFolderName('');
        setDescription('');
        setError(null);
        setIsSubmitting(false);
        onClose();
    };

    return (
        <Modal show={isOpen} onHide={handleClose}>
            <Form onSubmit={handleSubmit} noValidate>
                <Modal.Header closeButton>
                    <Modal.Title>{title}</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    {error && (
                        <div className="alert alert-danger mb-3">
                            {error}
                        </div>
                    )}

                    <Form.Group className="mb-3">
                        <Form.Label>Folder Name</Form.Label>
                        <div className="position-relative">
                            <div className="position-absolute top-50 translate-middle-y ps-3">
                                <FolderPlus size={16} className="text-muted" />
                            </div>
                            <Form.Control
                                type="text"
                                value={folderName}
                                onChange={(e) => {
                                    setFolderName(e.target.value);
                                    if (error) setError(null);
                                }}
                                isInvalid={!!error}
                                required
                                autoFocus
                                maxLength={255}
                                className="ps-5 border"
                                style={{ height: '38px' }}
                            />
                        </div>
                    </Form.Group>

                    <Form.Group>
                        <Form.Label>Description</Form.Label>
                        <div className="position-relative">
                            <div className="position-absolute top-50 translate-middle-y ps-3">
                                <FileText size={16} className="text-muted" />
                            </div>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                maxLength={1000}
                                className="ps-5 border"
                            />
                        </div>
                    </Form.Group>
                </Modal.Body>

                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={handleClose}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        type="submit"
                        disabled={!folderName.trim() || isSubmitting}
                    >
                        {isSubmitting ? 'Creating...' : 'Create Folder'}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default FolderDialog;